import {
    BusinessCenterRounded as BusinessCenterRoundedIcon,
    WorkOutlineOutlined as WorkOutlineOutlinedIcon,
} from '@mui/icons-material';
import is from '@sindresorhus/is';
import { useContext, useState } from 'react';

import { CardPlaceholder } from './common/CardPlaceholder';
import { HistoryItem } from './common/HistoryItem';
import { HistoryItemListWrapper } from './common/HistoryItemListWrapper';
import { ShowAllButton } from './common/ShowAllButton';
import { ApplicationContext } from '../../../../../../shared/source/context/context';
import { CardIconCaption } from '../../../../design/components/cards/CardIconCaption';
import { InlineLoadingSpinner } from '../../../../design/components/LoadingSpinner';
import { useUserHistoryOptionsQuery } from '../../../query-hooks/users/user-history';
import { useWorkHistoryQuery } from '../../../query-hooks/users/work-history';
import { useScrollToLocationHash } from '../../../use-scroll-to-location-hash';
import { createUserWorkHistoryUrl, editUserWorkHistoryUrl } from '../constants';
import { AddButton } from '../user-profile/common/ActionButtons';

import type { WorkHistoryItem } from '../../../../../../shared/source/data-contract/work-history';
import type { FunctionComponent } from 'react';

const WorkHistoryCaption: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);

    return (
        <CardIconCaption
            color="jobposting.main"
            icon={WorkOutlineOutlinedIcon}
            text={translate('pages.user-work-history.caption')}
        />
    );
};

type WorkHistoryContentProps = {
    readonly canUserAddWorkHistory?: boolean;
    readonly canUserEditWorkHistory?: boolean;
    readonly userId?: string;
    readonly workHistory?: ReadonlyArray<WorkHistoryItem>;
};

export const WorkHistoryContent: FunctionComponent<WorkHistoryContentProps> = ({
    canUserAddWorkHistory = false,
    canUserEditWorkHistory = false,
    userId,
    workHistory = [],
}) => {
    const { actions, translate } = useContext(ApplicationContext);
    const [showAll, setShowAll] = useState(false);
    const hasWorkHistory = workHistory.length > 0;
    const slicedWorks = showAll ? workHistory : workHistory.slice(0, 2);

    const headlineActions = canUserAddWorkHistory && is.nonEmptyString(userId) && (
        <AddButton onClick={() => actions.navigateTo(createUserWorkHistoryUrl(userId))} />
    );

    const editAction = (itemId: string): VoidFunction | undefined =>
        canUserEditWorkHistory && is.nonEmptyString(userId)
            ? () => actions.navigateTo(editUserWorkHistoryUrl(userId, itemId))
            : undefined;

    useScrollToLocationHash();

    return (
        <HistoryItemListWrapper
            anchorId="work-history"
            headline={translate('pages.recruiter-profile.background.work-history.title')}
            headlineActions={headlineActions}
        >
            {hasWorkHistory ? (
                slicedWorks.map((historyItem) => (
                    <HistoryItem
                        key={historyItem.id}
                        caption={<WorkHistoryCaption />}
                        dateFrom={historyItem.dateFrom}
                        dateTo={historyItem.dateTo}
                        skills={historyItem.skills}
                        skillsHeadline={translate('pages.user-work-history.skills-list-headline')}
                        subTitle={[historyItem.companyName, historyItem.location]}
                        title={historyItem.jobTitle}
                        onClickEdit={editAction(historyItem.id)}
                    />
                ))
            ) : (
                <CardPlaceholder
                    placeholderIcon={<BusinessCenterRoundedIcon />}
                    placeholderText={translate('pages.recruiter-profile.background.work-history.placeholder')}
                />
            )}
            {workHistory.length > 2 && <ShowAllButton showAll={showAll} onClick={() => setShowAll(!showAll)} />}
        </HistoryItemListWrapper>
    );
};

type WorkHistoryProps = {
    readonly userId: string;
};

export const WorkHistory: FunctionComponent<WorkHistoryProps> = ({ userId }) => {
    const { data: userHistoryOptions, isFetching: userHistoryOptionsIsFetching } = useUserHistoryOptionsQuery(userId);
    const { data: workHistory, isFetching: workHistoryIsFetching } = useWorkHistoryQuery(
        userId,
        userHistoryOptions?.canReadHistory ?? false,
    );

    if (userHistoryOptionsIsFetching || workHistoryIsFetching) {
        return <InlineLoadingSpinner />;
    }

    return (
        <WorkHistoryContent
            canUserAddWorkHistory={userHistoryOptions?.canEditHistory}
            canUserEditWorkHistory={userHistoryOptions?.canEditHistory}
            userId={userId}
            workHistory={workHistory}
        />
    );
};
